import { useEffect } from 'react';
import { Flex } from '@chakra-ui/react';
import { Accordion } from '@morressier/alembic';
import { SidebarLayout } from 'src/layouts/SidebarLayout';
import { RevisionSidebar } from '../SubmissionUpload/RevisionSidebar';
import StepHeader from '../SubmissionUpload/components/StepHeader';
import { Button } from '@morressier/alembic';
import { IRevisionRound } from '@publishing-engine/domain/models';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight
} from '@fortawesome/pro-regular-svg-icons';
import RevisionRequest from './RevisionRequest';
import { SubmissionTab } from 'src/components/Submission/MySubmission/SubmissionTab';
import { UnsubmitFeedbackAccordion } from 'src/components/Submission/MySubmission/components/feedback/UnsubmitFeedbackAccordion';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { GenericSubmissionField } from 'src/components/Submission/MySubmission/FormFieldGeneric/GenericSubmissionField';
import { scrollToPendingFileField } from '../SubmissionUpload/fileReplacementNavigation';

interface Props {
  currentStep: number;
  totalSteps: number;
  goToPreviousStep: () => void;
  goToNextStep: () => void;
  goToStep: (step: number) => void;
  onClose: () => void;
  revisionRound: IRevisionRound | undefined;
}

const SubmissionInformationStep: React.FC<Props> = ({
  currentStep,
  goToStep,
  totalSteps,
  onClose,
  revisionRound,
  goToNextStep,
  goToPreviousStep
}) => {
  useEffect(() => scrollToPendingFileField('details'), []);
  const {
    workflowResponse: {
      workflowData: { currentState }
    }
  } = useSubmissionData();
  const isUnsubmitted = currentState === 'unsubmitted';

  return (
    <>
      <Flex
        flexDirection="column"
        backgroundColor="surfaceSecondaryFill"
        minH="100vh"
      >
        <SidebarLayout
          sidebar={
            <RevisionSidebar
              currentStep={currentStep}
              onNavigate={step => {
                goToStep(step);
              }}
            />
          }
        >
          <StepHeader
            currentStep={currentStep + 1}
            totalSteps={totalSteps}
            heading="Submission Information"
            description={
              isUnsubmitted
                ? 'Review and edit the extracted content, such as the submission’s title, abstract and keywords, ensuring that all required information is included.'
                : 'Review and edit the content, such as the submission’s title, abstract and keywords, ensuring that all required information is included.'
            }
          />
          {revisionRound && <RevisionRequest revisionRound={revisionRound} />}
          <UnsubmitFeedbackAccordion />
          {isUnsubmitted && (
            <Accordion
              chakraAccordionProps={{
                allowMultiple: true,
                defaultIndex: [0],
                width: '100%',
                background: 'surfacePrimaryFill',
                marginBottom:'10px'
              }}
              accordionItemProps={{ marginBottom: 0 }}
              items={[
                {
                  title: (
                    <Flex id="Submission Files" textStyle="headingGroup">
                      Submission Files
                    </Flex>
                  ),
                  content: (
                    <Flex flexDirection="column" gap={4}>
                      <GenericSubmissionField
                        fieldName="Manuscript"
                        isUpdateRequired
                      />
                      <GenericSubmissionField
                        fieldName="TitlePage"
                        isUpdateRequired
                      />
                    </Flex>
                  )
                }
              ]}
            />
          )}
          <SubmissionTab isUpdateRequired />
        </SidebarLayout>
      </Flex>
      <Flex
        padding={4}
        bottom={0}
        zIndex={1}
        justifyContent="center"
        position="sticky"
        bgColor="surfacePrimaryFill"
        marginX="auto"
      >
        <Flex width="100%" justifyContent="space-between">
          {/* ----- Footer Buttons ----- */}
          <Button
            variant="caution"
            onClick={() => {
              onClose();
            }}
          >
            Exit
          </Button>
          <Flex gap={4} flexDirection="row">
            <Button
              variant="secondary"
              leftIcon={<FontAwesomeIcon icon={faChevronLeft} />}
              onClick={goToPreviousStep}
            >
              Back
            </Button>
            <Button variant="primary" onClick={goToNextStep}>
              Next
              <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default SubmissionInformationStep;
